import { AllIntegrations } from '@/hooks/useIntegration';
import type { DropdownOption } from '@/types/dropdown.types';
import { normalizeOriginName } from '@/utils/statisticValidation.util';

type GroupOption = DropdownOption & {
  isGroup?: boolean;
  items?: DropdownOption[];
};

/**
 * Formats a raw source value for display using the same logic as the main tables.
 * Converts backend values like "self", "account" to user-friendly names like "Chariot".
 */
export const formatSourceForDisplay = (
  rawSource: string | undefined | null,
  sourceFilterOptions: GroupOption[] = [],
  _isPraetorianUser = false,
  _isDemoOrg = false
): string => {
  if (!rawSource) {
    return 'Chariot';
  }

  // Find Chariot group and its values (normalize to lowercase)
  const chariotGroup = sourceFilterOptions.find(
    sf => sf.isGroup && sf.label === 'Chariot'
  );
  const chariotSourceValues =
    chariotGroup?.items?.map(item => String(item.value).toLowerCase()) || [];

  // If the source is a Chariot capability, always show 'Chariot' (case-insensitive)
  if (chariotSourceValues.includes(String(rawSource).toLowerCase())) {
    return 'Chariot';
  }

  // Existing logic for integrations and manual
  const lowerSource = String(rawSource).toLowerCase();
  const isIntegration = Object.keys(AllIntegrations).includes(lowerSource);
  const isManual = rawSource.endsWith('@praetorian.com');

  const getFormattedLabel = (sourceValue: string): string => {
    let foundLabel: string | undefined;

    const chariotGroupFromFilters = sourceFilterOptions.find(
      sf => sf.isGroup && sf.label === 'Chariot'
    );
    if (chariotGroupFromFilters?.items) {
      foundLabel = chariotGroupFromFilters.items.find(
        item => item.value === sourceValue
      )?.label;
    }

    if (!foundLabel) {
      const integrationOption = sourceFilterOptions.find(
        option => !option.isGroup && option.value === sourceValue
      );
      foundLabel = integrationOption?.label;
    }

    return foundLabel || normalizeOriginName(sourceValue);
  };

  if (isManual) {
    return 'Manual';
  }

  if (isIntegration) {
    return getFormattedLabel(rawSource);
  }

  // Handle common Chariot source values that should display as "Chariot"
  const chariotSources = [
    'self',
    'account',
    'seed',
    'provided',
    'adhoc',
    '@praetorian.com',
  ];

  if (
    chariotSources.some(source =>
      String(rawSource).toLowerCase().includes(source.toLowerCase())
    )
  ) {
    return 'Chariot';
  }

  // Robust fallback: any non-integration, non-manual source is 'Chariot'
  return 'Chariot';
};

// Utility to aggregate all unique sources for an asset or list of assets
// Returns a sorted array of unique sources (integrationSource, allSources, source)

export function aggregateAllSourcesForAsset(
  assetOrAssets: Record<string, any> | Array<Record<string, any>>
): string[] {
  const assets = Array.isArray(assetOrAssets) ? assetOrAssets : [assetOrAssets];
  const sourceSet = new Set<string>();

  assets.forEach(asset => {
    // Add allSources if present (array)
    if (Array.isArray(asset.allSources)) {
      asset.allSources.forEach((src: string) => {
        if (src && typeof src === 'string') sourceSet.add(src);
      });
    }
    // Add integrationSource if present
    if (
      asset.integrationSource &&
      typeof asset.integrationSource === 'string'
    ) {
      sourceSet.add(asset.integrationSource);
    }
    // Add source if present
    if (asset.source && typeof asset.source === 'string') {
      sourceSet.add(asset.source);
    }
  });

  // Remove empty or dash values
  const filtered = Array.from(sourceSet).filter(src => src && src !== '-');
  // Sort alphabetically
  return filtered.sort((a, b) => a.localeCompare(b));
}
